/**
 * offlineData.js — read cache + write queue for the app's offline mode.
 *
 * READS: a whitelist of small, high-value GETs is snapshotted to localStorage
 * on every success; when the network drops, api.js serves the snapshot with
 * `response.offline = true` so screens render last-known data instead of an
 * error card. The whitelist is deliberately tiny — this is "open the app in
 * the warehouse dead zone", not a sync engine.
 *
 * WRITES: only approval decisions queue (they are role-scoped and the server
 * rejects an already-decided record cleanly on replay). Attendance punches
 * are deliberately NOT queued — the server stamps punch time on arrival, so a
 * replayed clock-in would falsify the attendance record.
 */

const CACHE_PREFIX = 'aone_offline:';
const QUEUE_KEY = 'aone_offline_queue';
const MAX_QUEUE = 50;

// Path (query stripped) → cache. Keep each entry small; localStorage is ~5MB.
const CACHE_WHITELIST = new Set([
  '/dashboard/bundle',
  '/approvals/pending',
  '/tasks',
  '/notifications',
]);

const keyOf = (url) => CACHE_PREFIX + String(url || '').split('?')[0];

export function isCacheable(url) {
  return CACHE_WHITELIST.has(String(url || '').split('?')[0]);
}

export function cacheSet(url, data) {
  try {
    localStorage.setItem(keyOf(url), JSON.stringify({ at: Date.now(), data }));
  } catch { /* quota / private mode — cache is best-effort */ }
}

export function cacheGet(url) {
  try {
    const raw = localStorage.getItem(keyOf(url));
    if (!raw) return null;
    return JSON.parse(raw); // { at, data }
  } catch { return null; }
}

// A request that never reached the server: no HTTP response, not a timeout
// the server may still be processing meaningfully for reads either way.
export function isNetworkError(err) {
  return !!err && !err.response && (err.code === 'ERR_NETWORK' || err.message === 'Network Error' || !navigator.onLine);
}

// ── Write queue (approval decisions only) ───────────────────────────────────

function readQueue() {
  try { const v = JSON.parse(localStorage.getItem(QUEUE_KEY)); return Array.isArray(v) ? v : []; } catch { return []; }
}
function writeQueue(list) {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(list.slice(0, MAX_QUEUE))); } catch { /* best-effort */ }
}

export function enqueueWrite({ method, url, body, label }) {
  const list = readQueue();
  list.push({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, method, url, body: body || null, label: label || url, at: new Date().toISOString() });
  writeQueue(list);
  return list.length;
}

export function queuedCount() { return readQueue().length; }

/**
 * Replay everything in order. Any 4xx means the record moved on while we were
 * offline (someone else decided it) — that item is dropped, not retried.
 * Another network failure stops the run and keeps the remainder queued.
 */
export async function replayQueue(api) {
  let list = readQueue();
  if (!list.length) return { sent: 0, dropped: 0, remaining: 0 };
  let sent = 0, dropped = 0;
  while (list.length) {
    const item = list[0];
    try {
      await api.request({ method: item.method, url: item.url, data: item.body || undefined });
      sent++;
      list = list.slice(1);
      writeQueue(list);
    } catch (e) {
      if (e?.response && e.response.status >= 400 && e.response.status < 500) {
        dropped++; // decided elsewhere / no longer valid — don't loop forever
        list = list.slice(1);
        writeQueue(list);
      } else {
        break; // still offline (or 5xx) — keep the rest for the next reconnect
      }
    }
  }
  return { sent, dropped, remaining: list.length };
}

/**
 * Remove every trace of the signed-in user's offline data.
 *
 * Logout used to delete the bearer token and nothing else, so the snapshots —
 * dashboard, tasks, approvals, notifications — and the queued approval writes
 * stayed in localStorage. On a shared machine the next person to sign in saw
 * the previous user's work until each cache was overwritten, and a queued
 * approval could replay under their session.
 */
export function purgeOfflineData() {
  try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith(CACHE_PREFIX)) localStorage.removeItem(key);
    }
    localStorage.removeItem(QUEUE_KEY);
  } catch { /* storage unavailable — nothing to purge */ }
}
